'use client'

import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrgNavigate } from '@/hooks/use-app-navigate'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { AlertTriangle } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { toast } from 'react-hot-toast'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { useProcessorById, useUpdateProcessor } from '@/lib/api/processors'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'
import { SandboxProcessorDetailsSkeleton } from '../../components/processor-details-skeleton'

const SandboxEditPageInner = () => {
    const { t } = useTranslation('processors')
    const id = useProcessorEditId()
    const { orgRoutes, navigate } = useOrgNavigate()
    const processorExit = useProcessorEditExit()

    const { data: processor, isLoading } = useProcessorById(id!)
    const updateProcessorMutation = useUpdateProcessor()

    const [validationError, setValidationError] = useState<string | null>(null)

    const options = processor?.options as Record<string, unknown> | undefined

    const [formData, setFormData] = useState({
        name: processor?.name || '',
        enabled: processor?.enabled ?? true,
        forceDecline: (options?.forceDecline as boolean) ?? false,
        dryMode: (options?.dryMode as boolean) ?? false,
        targetApprovalRate: (options?.targetApprovalRate as number | undefined) ?? undefined,
    })

    const handleCancel = () => {
        if (processorExit.isEmbed) {
            processorExit.exitCancel()
            return
        }
        navigate(orgRoutes.PROCESSOR_DETAILS(id!))
    }

    const handleSubmit = () => {
        if (!formData.name.trim()) {
            setValidationError(t('nameRequired'))
            return
        }

        if (
            formData.targetApprovalRate !== undefined &&
            (formData.targetApprovalRate < 0 || formData.targetApprovalRate > 100)
        ) {
            setValidationError(t('invalidApprovalRate'))
            return
        }

        setValidationError(null)

        updateProcessorMutation.mutate(
            {
                processorId: id!,
                updatedData: {
                    name: formData.name,
                    type: 'sandbox',
                    enabled: formData.enabled,
                    options: {
                        testMode: true, // Always in test mode for sandbox
                        dryMode: formData.dryMode,
                        forceDecline: formData.forceDecline,
                        targetApprovalRate: formData.forceDecline ? undefined : formData.targetApprovalRate,
                    },
                },
            },
            {
                onSuccess: () => {
                    toast.success(t('processorUpdated'))
                    if (processorExit.isEmbed) {
                        processorExit.exitAfterSave()
                        return
                    }
                    navigate(orgRoutes.PROCESSOR_DETAILS(id!))
                },
                onError: () => {
                    toast.error(t('processorUpdateFailed'))
                },
            }
        )
    }

    if (isLoading || !processor) {
        return <SandboxProcessorDetailsSkeleton />
    }

    return (
        <div className="container mx-auto py-8">
            <ProcessorEditHeader
                processorId={processor.id}
                title={t('editSandboxConfiguration')}
                onCancel={handleCancel}
                onSave={handleSubmit}
                isSaving={updateProcessorMutation.isPending}
            />

            {validationError && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                </Alert>
            )}

            <div className="space-y-6">
                {/* General Settings Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>{t('generalSettings')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="name">{t('processorName')} *</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={t('processorNamePlaceholder')}
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                {t('sandboxProcessorNameDescription')}
                            </p>
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="enabled"
                                checked={formData.enabled}
                                onCheckedChange={(checked) =>
                                    setFormData({ ...formData, enabled: checked === true })
                                }
                            />
                            <Label htmlFor="enabled">{t('enabled')}</Label>
                        </div>
                    </CardContent>
                </Card>

                {/* Transaction Behavior Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>{t('transactionBehavior')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="forceDecline"
                                checked={formData.forceDecline}
                                onCheckedChange={(checked) =>
                                    setFormData({
                                        ...formData,
                                        forceDecline: checked === true,
                                        targetApprovalRate: checked === true ? undefined : formData.targetApprovalRate,
                                    })
                                }
                            />
                            <Label htmlFor="forceDecline">{t('forceAllTransactionsToDecline')}</Label>
                        </div>
                        <p className="text-sm text-muted-foreground ml-6">
                            {t('forceDeclineDescription')}
                        </p>

                        {!formData.forceDecline && (
                            <div>
                                <Label htmlFor="targetApprovalRate">
                                    {t('targetApprovalRateLabel')} (%)
                                </Label>
                                <Input
                                    id="targetApprovalRate"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={formData.targetApprovalRate ?? ''}
                                    onChange={(e) => {
                                        const value = e.target.value === '' ? undefined : parseInt(e.target.value)
                                        setFormData({
                                            ...formData,
                                            targetApprovalRate: value,
                                        })
                                    }}
                                    placeholder={t('targetApprovalRatePlaceholder')}
                                />
                                <p className="text-sm text-muted-foreground mt-1">
                                    {t('targetApprovalRateDescription2')}
                                </p>
                            </div>
                        )}

                        <div className="rounded-lg bg-amber-50 p-3 border border-amber-200">
                            <p className="text-sm text-amber-700">
                                <span className="font-medium">{t('priority')}:</span>{' '}
                                {t('sandboxBehaviorPriority')}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Sandbox Information Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>{t('sandboxInformation')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                            <div className="space-y-3">
                                <p className="text-sm text-blue-700">
                                    <span className="font-medium">🧪 {t('testEnvironment')}:</span>{' '}
                                    {t('sandboxTestEnvironmentDescription')}
                                </p>
                                <ul className="list-disc space-y-1 pl-4 text-sm text-blue-700">
                                    <li>{t('sandboxInfo1')}</li>
                                    <li>{t('sandboxInfo2')}</li>
                                    <li>{t('sandboxInfo3')}</li>
                                    <li>{t('sandboxInfo4')}</li>
                                    <li>{t('sandboxInfo5')}</li>
                                    <li>{t('sandboxInfo6')}</li>
                                </ul>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Test Cards Section */}
                <Card>
                    <CardHeader>
                        <CardTitle>{t('testCardNumbers')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-lg bg-green-50 p-4 border border-green-200">
                            <h4 className="mb-2 text-sm font-medium text-green-800">
                                {t('successfulCards')}
                            </h4>
                            <div className="space-y-2 text-sm text-green-700">
                                <div className="font-mono">4242424242424242</div>
                                <div className="font-mono">5555555555554444</div>
                                <div className="font-mono">378282246310005</div>
                                <p className="text-xs mt-2">{t('successfulCardsNote2')}</p>
                            </div>
                        </div>

                        <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                            <h4 className="mb-2 text-sm font-medium text-red-800">
                                {t('declinedCards')}
                            </h4>
                            <div className="space-y-2 text-sm text-red-700">
                                <div className="font-mono text-xs">4000000000000002 - {t('genericDecline')}</div>
                                <div className="font-mono text-xs">4000000000009995 - {t('insufficientFunds')}</div>
                                <div className="font-mono text-xs">4000000000000069 - {t('expiredCard')}</div>
                                <p className="text-xs mt-2">{t('declinedCardsNote2')}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
            </div>
        </div>
    )
}

export const SandboxEditPage = withRequiredPermission(SandboxEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)

